import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { TwMainLayout } from "../../../components/tailwind";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { clearLoadingData } from "../../../redux/reducers/loadingReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { creditsOf, describe, FREE_TRIAL_NAME } from "../../classes/products/productUtils";
import { clubIdOf, useClub, useMemberPurchases } from "./portalUtils";

/**
 * Club Webshop (new design). Same requests as the old ConsumersProductList:
 *  - GET products/list?page=n&limit=10&businessId=<club>  (all pages), only products with availableForSale
 *  - Buy: POST createCheckoutSessionsOfUserPurchase {productId, username, fullName, email, success_url, cancel_url}
 *    and go to Stripe Checkout in the same tab. Paid → /members/purchases/success (My Purchases),
 *    cancelled → /members/webshop/cancelled. The backend appends "?price_id=…&customer_id=…", so these URLs carry no query.
 * "You have this" = the member has an active purchase of that product.
 */

function ProductCard({ p, owned, busy, onBuy }) {
  const d = describe(p);
  const isSub = p.productType === "subscription";
  return (
    <li className="flex flex-col gap-3 rounded-3xl border border-gray-200 bg-white p-5 shadow-[0_10px_30px_rgba(72,27,58,0.05)]">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-[17px] font-black leading-snug text-gray-900">{p.productName}</h3>
        {owned && <span className="shrink-0 rounded-full bg-[#E8F7EE] px-2.5 py-0.5 text-xs font-extrabold text-[#1F6B3A]">You have this</span>}
      </div>
      <div className="text-[30px] font-black leading-none tracking-tight text-gray-900">
        {parseFloat(p.price) > 0 ? d.priceMain : "Free"}
        {isSub && <span className="ml-1 text-[15px] font-bold text-gray-500">{d.priceSub}</span>}
      </div>
      <ul className="flex flex-col gap-1.5 text-sm text-gray-700">
        <li>{d.kind === "free" ? `${creditsOf(p)} ${creditsOf(p) === 1 ? "credit" : "credits"}` : d.credits}</li>
        {d.validity && <li>{d.validity}</li>}
        <li className="text-gray-500">For {d.classes === "All activities" ? "all classes" : d.classes}</li>
      </ul>
      {p.description && <p className="line-clamp-3 text-[13px] text-gray-500">{p.description}</p>}
      <button
        type="button"
        disabled={busy}
        onClick={() => onBuy(p)}
        className="mt-auto flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-brown-coffee text-[15px] font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60"
      >
        {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
        {isSub ? "Subscribe" : "Buy"}
      </button>
    </li>
  );
}

export default function WebshopPage({ cancelled = false }) {
  const dispatch = useDispatch();
  const { addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const club = useClub();
  const clubId = clubIdOf(userReduxState.userData);
  const { summary } = useMemberPurchases();
  const [products, setProducts] = React.useState(null);
  const [buying, setBuying] = React.useState("");

  React.useEffect(() => {
    dispatch(clearLoadingData()); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!clubId) return;
    let alive = true;
    (async () => {
      const all = [];
      try {
        for (let page = 1; page <= 10; page++) {
          const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}products/list?page=${page}&limit=10&businessId=${clubId}` });
          const list = res.status === 200 && Array.isArray(res.data?.data?.data) ? res.data.data.data : [];
          all.push(...list);
          if (!list.length || all.length >= (parseInt(res.data?.data?.total, 10) || 0)) break;
        }
      } catch (e) {
        /* show what we have */
      }
      if (alive) setProducts(all.filter((p) => p.availableForSale && p.productName !== FREE_TRIAL_NAME && ["classpass", "subscription"].includes(p.productType)));
    })();
    return () => {
      alive = false;
    };
  }, [clubId]);

  const buy = async (p) => {
    const user = userReduxState.userData || {};
    setBuying(p._id);
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings("")}createCheckoutSessionsOfUserPurchase`,
        data: {
          productId: p._id,
          username: user.username,
          fullName: user.fullName,
          email: user.email || "",
          success_url: `${window.location.origin}/members/purchases/success`,
          cancel_url: `${window.location.origin}/members/webshop/cancelled`,
        },
      });
      if (res.status === 200 && res.data.statusCode === 200 && res.data.data?.checkoutSession?.url) {
        window.location.assign(res.data.data.checkoutSession.url);
      } else {
        addErrorToast((res.data && typeof res.data.message === "string" && res.data.message) || "Something went wrong. Please try again.");
      }
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBuying("");
    }
  };

  const passes = (products || []).filter((p) => p.productType === "classpass");
  const subs = (products || []).filter((p) => p.productType === "subscription");
  const owned = (p) => summary.ownedProductIds.includes(String(p._id));

  const Group = ({ title, sub, items }) =>
    items.length > 0 && (
      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h2 className="text-lg font-black text-gray-900">{title}</h2>
          <span className="text-[13px] text-gray-500">{sub}</span>
        </div>
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <ProductCard key={p._id} p={p} owned={owned(p)} busy={buying === p._id} onBuy={buy} />
          ))}
        </ul>
      </section>
    );

  return (
    <TwMainLayout
      userType={"student"}
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <div className="mx-auto flex max-w-6xl flex-col gap-6">
            <div>
              <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-[28px]">Club Webshop</h1>
              <p className="mt-1 text-sm text-gray-600 sm:text-[15px]">
                Buy a credit pass or subscription from {club.name}. You pay securely with iDEAL, card, Apple Pay or Google Pay.
              </p>
            </div>
            {cancelled && (
              <div role="status" className="rounded-2xl border border-[#F5D7A8] bg-[#FFF4E5] px-4 py-3.5 text-sm text-[#5A3200]">
                <span className="font-extrabold">Payment cancelled.</span> Nothing was charged. You can pick a pass or subscription again whenever you like.
              </div>
            )}
            {products === null ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-64 animate-pulse rounded-3xl bg-[#F3EEF5]" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className={classNames("flex flex-col items-center gap-2 rounded-3xl border border-gray-200 bg-white px-6 py-12 text-center")}>
                <div className="text-base font-black text-gray-900">Nothing for sale yet</div>
                <div className="max-w-md text-sm text-gray-500">{club.name} hasn't put passes or subscriptions online yet. Ask the club how to buy one.</div>
              </div>
            ) : (
              <>
                <Group title="Credit passes" sub="A set number of credits, 1 credit per class" items={passes} />
                <Group title="Subscriptions" sub="Billed automatically every period" items={subs} />
              </>
            )}
          </div>
        </div>
      }
    />
  );
}
